import React from 'react';
import { cn } from '@utils/index';

export interface InlineIconProps {
  /** Source URL of the SVG icon */
  src: string;
  /** Alt text for accessibility */
  alt: string;
  /** Additional CSS classes */
  className?: string;
  classNameSVG?: string;
  /** Width of the icon */
  width?: string | number;
  /** Height of the icon */
  height?: string | number;
  /** Whether to show a loading state */
  showLoading?: boolean;
  /** Loading component to display while fetching */
  loadingComponent?: React.ReactNode;
  /** Fallback component when icon fails to load */
  fallbackComponent?: React.ReactNode;
  /** Additional props for the container span */
  containerProps?: React.HTMLAttributes<HTMLSpanElement>;
}

export const InlineIcon = React.memo<InlineIconProps>(function InlineIcon({
  src,
  alt,
  className,
  classNameSVG,
  width = 'w-full',
  height = 'h-full',
  showLoading = false,
  loadingComponent,
  fallbackComponent,
  containerProps = {},
}: InlineIconProps) {
  const [svgContent, setSvgContent] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;
    
    if (!src) {
      setSvgContent(null);
      setHasError(false);
      return;
    }

    if (src.toLowerCase().endsWith('.svg')) {
      setIsLoading(true);
      setHasError(false);
      
      fetch(src)
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Failed to load SVG: ${res.status}`);
          }
          return res.text();
        })
        .then((text) => {
          if (!isMounted) return;
          
          try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, 'image/svg+xml');
            const svg = doc.documentElement;
            
            // Check for parsing errors
            const parseError = doc.querySelector('parsererror');
            if (parseError) {
              throw new Error('SVG parsing failed');
            }

            const existingClass = svg.getAttribute('class') || '';
            const mergedClass = `${classNameSVG} ${existingClass}`.trim();
            svg.setAttribute('class', mergedClass);

            // Add width and height attributes if they're numbers
            if (typeof width === 'number') {
              svg.setAttribute('width', width.toString());
            }
            if (typeof height === 'number') {
              svg.setAttribute('height', height.toString());
            }

            const serialized = new XMLSerializer().serializeToString(svg);
            setSvgContent(serialized);
            setHasError(false);
          } catch (error) {
            console.warn('Failed to parse SVG, using raw content:', error);
            setSvgContent(text);
            setHasError(false);
          }
        })
        .catch((error) => {
          console.error('Failed to load SVG:', error);
          if (isMounted) {
            setSvgContent(null);
            setHasError(true);
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoading(false);
          }
        });
    } else {
      setSvgContent(null);
      setHasError(false);
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [src, width, height]);

  // Show loading state
  if (isLoading && showLoading) {
    return (
      <span
        className={cn(className)}
        role="img"
        aria-label={`Loading ${alt}`}
        {...containerProps}
      >
        {loadingComponent || (
          <div className="animate-pulse bg-gray-300 rounded w-full h-full" />
        )}
      </span>
    );
  }

  // Show error fallback
  if (hasError && fallbackComponent) {
    return (
      <span
        className={cn(className)}
        role="img"
        aria-label={`Error loading ${alt}`}
        {...containerProps}
      >
        {fallbackComponent}
      </span>
    );
  }

  // Show SVG content
  if (svgContent) {
    return (
      <span
        className={cn(className)}
        role="img"
        aria-label={alt}
        dangerouslySetInnerHTML={{ __html: svgContent }}
        {...containerProps}
      />
    );
  }

  // Show fallback for non-SVG or when no content
  if (fallbackComponent) {
    return (
      <span
        className={cn(className)}
        role="img"
        aria-label={alt}
        {...containerProps}
      >
        {fallbackComponent}
      </span>
    );
  }

  return null;
});
